import { useCallback } from "react";

import confetti from "canvas-confetti";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";


// Point values for different activities
const POINTS = {
  session: 10,
  message: 2,
  memory: 15,
  project: 25,
  board: 50,
};

export const useGamification = () => {
  const { user } = useAuth();

  const awardPoints = useCallback(async (activityType: keyof typeof POINTS) => {
    if (!user) return;

    const points = POINTS[activityType];

    try {
      const { data, error } = await supabase.rpc('award_points', {
        p_user_id: user.id,
        p_activity_type: activityType,
        p_points: points,
      });

      if (error) {
        console.error('Error awarding points:', error);
        return;
      }

      const result = data as any;

      // Show level up notification with confetti
      if (result?.level_up) {
        // Trigger confetti for level up
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#FFD700', '#FFA500', '#FF6347'],
        });
        
        toast.success(`🎉 Level Up! You reached level ${result.new_level}!`, {
          duration: 5000,
          className: 'animate-scale-in',
        });
      }

      // Show achievement notifications with confetti
      if (result?.new_achievements && Array.isArray(result.new_achievements) && result.new_achievements.length > 0) {
        result.new_achievements.forEach((ach: any, index: number) => {
          // Stagger confetti for multiple achievements
          setTimeout(() => {
            // Trigger confetti burst for achievement
            confetti({
              particleCount: 50,
              angle: 60 + (index * 20),
              spread: 55,
              origin: { x: 0.5, y: 0.5 },
              colors: ['#FFD700', '#00FF00', '#00FFFF', '#FF00FF'],
            });
            
            toast.success(
              <div className="flex items-center gap-2 animate-fade-in">
                <span className="text-2xl animate-scale-in">{ach.icon}</span>
                <div>
                  <p className="font-bold">Achievement Unlocked!</p>
                  <p className="text-sm">{ach.name}</p>
                  <p className="text-xs text-muted-foreground">+{ach.points_reward} points</p>
                </div>
              </div>,
              {
                duration: 6000,
                className: 'bg-gradient-to-r from-primary/20 to-background border-primary',
              }
            );
          }, index * 500);
        });
      }

      // Show points earned (subtle)
      if (result?.points_awarded > 0 && !result?.level_up) {
        toast.success(`+${result.points_awarded} XP`, {
          duration: 2000,
          className: 'text-sm',
        });
      }
    } catch (error) {
      console.error('Failed to award points:', error);
    }
  }, [user]);

  return { awardPoints };
};
